import React, { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Button, Dropdown, Icon } from 'semantic-ui-react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { INSERT_TABLE_COMMAND } from '@lexical/table';

import { useUpload } from 'src/hooks';
import {
  ImagePayload,
  INSERT_IMAGE_COMMAND,
  INSERT_LOADING_IMAGE_COMMAND,
} from 'src/components/custom/Editor/nodes/ImageNode';
import { LexicalCommand } from 'lexical';
import { useSetting } from 'src/components/custom/Editor/hooks';
import { Modal } from 'src/components/custom/index';
import Form from 'src/components/form';
import { Typography } from 'src/components/coreUI';

const Insert: React.FC = () => {
  const { editable } = useSetting();

  return (
    <Dropdown
      disabled={!editable}
      icon={null}
      trigger={
        <button
          className="toolbar-item block-controls"
          aria-label="Insert"
          disabled={!editable}
        >
          <span className={'icon upload'} />
          <span className="text">Insert</span>
          <i className="chevron-down" />
        </button>
      }
    >
      <Dropdown.Menu>
        <UploadImageItem />
        <TableItem />
      </Dropdown.Menu>
    </Dropdown>
  );
};

const UploadImageItem: React.FC = () => {
  const [editor] = useLexicalComposerContext();
  const { handleUpload } = useUpload();

  const { open } = useDropzone({
    multiple: false,
    accept: {
      'image/png': ['.png', '.jpeg'],
    },
    async onDrop(files) {
      if (files.length) {
        editor.dispatchCommand(INSERT_LOADING_IMAGE_COMMAND, {
          name: files[0].name,
        });
        const returned = await handleUpload(files[0]);

        editor.dispatchCommand<LexicalCommand<ImagePayload>>(
          INSERT_IMAGE_COMMAND,
          {
            name: returned.origin.name,
            src: returned.url!,
            altText: 'editor',
          },
        );
      }
    },
  });

  return (
    <Dropdown.Item onClick={open}>
      <div className="d-flex">
        <Icon name="file image outline" /> <span>Image</span>
      </div>
    </Dropdown.Item>
  );
};

const TableItem: React.FC = () => {
  const [editor] = useLexicalComposerContext();
  const [visible, setVisible] = useState(false);
  const [{ rows, columns, header }, setTableData] = useState<{
    rows: string;
    columns: string;
    header: boolean;
  }>({ rows: '1', columns: '1', header: false });

  const click = () => {
    editor.dispatchCommand(INSERT_TABLE_COMMAND, {
      columns: String(columns),
      rows: String(rows),
      includeHeaders: header,
    });
  };

  return (
    <React.Fragment>
      <Dropdown.Item onClick={() => setVisible(true)}>
        <div className="d-flex">
          <Icon name="table" /> <span>Table</span>
        </div>
      </Dropdown.Item>

      <Modal
        visible={visible}
        onClose={() => setVisible(false)}
        content={() => (
          <Form>
            <Typography.Title>CREATE TABLE</Typography.Title>
            <Form.Group>
              <Form.Input
                label="COLUMNS"
                type="number"
                min={1}
                value={columns}
                onChange={(e) =>
                  setTableData({ rows, header, columns: e.target.value })
                }
                width={8}
              />
              <Form.Input
                label="ROWS"
                min={1}
                type="number"
                value={rows}
                onChange={(e) =>
                  setTableData({ columns, header, rows: e.target.value })
                }
                width={8}
              />
            </Form.Group>
            <Form.Checkbox
              label="With header?"
              checked={header}
              onChange={(e, data) =>
                setTableData({ rows, columns, header: !!data.checked })
              }
            />

            <div className="d-flex-right">
              <Button
                primary
                onClick={() => {
                  click();
                  setVisible(false);
                }}
              >
                Create
              </Button>
            </div>
          </Form>
        )}
      />
    </React.Fragment>
  );
};

export default Insert;
